import React from 'react';
import Page from 'components/UI/Page';
import BusinessUnitUsersTable from './BusinessUnitUsersTable';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { useAdminUsers } from 'hooks/useAdminUsers';
import Modal from 'components/UI/Modal';
import { Main } from 'styles/themeColors';
import { ADMINS_COLUMNS, USER_FIELDS } from 'constants/usersBusinessUnit';
import { USER_ROLE_MAPPER } from 'constants/user';
import SearchComponent from 'components/UI/SearchComponent';
import { GENERAL_CONSTANTS } from 'constants/general';
import { ORGANIZATION_CONSTANTS } from 'constants/organization';
import { useUserDeletion } from 'hooks/useUserDeletion';
import styled from 'styled-components';
import Vendor from 'data/Vendor';

const FilterRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    margin-bottom: 16px;
    gap: 16px;

    > div {
        width: 25%;
    }
`;

interface Props {
    toggleInviteModal: () => void;
    setUserProfileId: (id: number) => void;
    handleInviteInputChange: (field: string, value: any) => void;
    reload: boolean;
    setReload: (reload: boolean) => void;
    vendorsList: Vendor[];
    actionsDisabled?: boolean;
}

const AdminsTab: React.FC<Props> = ({
    toggleInviteModal,
    setUserProfileId,
    handleInviteInputChange,
    reload,
    setReload,
    vendorsList,
    actionsDisabled = false
}) => {
    // Centralized state management using the useAdminUsers hook
    const {
        tableData,
        loading,
        currentPage,
        rowsPerPage,
        sortBy,
        sortOrder,
        hasInitialLoad,
        tableKey,
        selectedRows,
        // Actions
        setSelectedRows,
        handlePageChange,
        handleRowsPerPageChange,
        handleSortChange,
        handleRemove,
        handleDeleteMultiple,
        searchTerm,
        setSearchTerm
    } = useAdminUsers({
        reload,
        setReload
    });

    // User deletion logic using the custom hook
    const {
        showDeleteModal,
        handleRemoveUser,
        handleBulkRemoveUser,
        handleProfileDeletion,
        onCloseDeleteModal
    } = useUserDeletion({ handleRemove, handleDeleteMultiple });

    // Custom handlers for editing admin
    const handleEditUser = (userProfileId: number) => {
        toggleInviteModal();
        setUserProfileId(userProfileId);
        if (tableData?.data?.length > 0) {
            const user = tableData.data.find((u) => u.id === userProfileId);
            if (user) {
                handleInviteInputChange(USER_FIELDS.NAME as any, user.name || '');
                handleInviteInputChange(USER_FIELDS.EMAIL as any, user.email || '');
                handleInviteInputChange(USER_FIELDS.USER_ROLE as any, { id: user.role, value: USER_ROLE_MAPPER[user.role] });
                handleInviteInputChange(USER_FIELDS.VENDOR as any, user.vendorId || '');
                handleInviteInputChange(USER_FIELDS.GIN_ID as any, user.ginId || '');
            }
        }
    };

    // Show loading state during initial load
    if (loading && !hasInitialLoad) {
        return (
            <Page>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <LoadingIndicator />
                </div>
            </Page>
        );
    }

    return (
        <>
            <FilterRow>
                <div>
                    <SearchComponent
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        placeHolder={GENERAL_CONSTANTS.SEARCH_EMAIL_USERNAME}
                        padding={'0'}
                        useGrid={false}
                    />
                </div>
            </FilterRow>

            {/* Reuse BusinessUnitUsersTable with admin-specific columns */}
            <BusinessUnitUsersTable
                // Data props
                data={tableData.data}
                totalCount={tableData.totalCount}
                loading={loading}
                tableKey={tableKey}
                columns={ADMINS_COLUMNS}

                // Pagination props
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}

                // Sorting props
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}

                // Selection props
                selectedRows={selectedRows}
                onSelectionChange={setSelectedRows}

                // Action handlers
                onEdit={handleEditUser}
                onRemove={handleRemoveUser}
                onRemoveBulkUser={() => handleBulkRemoveUser(selectedRows)}

                // UI state - admins always have edit/delete capabilities
                isVendorOnly={false}
                hasInitialLoad={hasInitialLoad}
                actionsDisabled={actionsDisabled}
            />

            <Modal
                showModal={showDeleteModal}
                onClose={onCloseDeleteModal}
                title={GENERAL_CONSTANTS.CONFIRM_DELETE}
                primaryButtonText={GENERAL_CONSTANTS.DELETE}
                ghostButtonText={GENERAL_CONSTANTS.CANCEL}
                primaryButtonColor={Main.brandDark}
                color={Main.black}
                borderColor={Main.disabled}
                onClick={handleProfileDeletion}
            >
                {ORGANIZATION_CONSTANTS.DELETE_ADMIN_CONFIRMATION}
            </Modal>
        </>
    );
};

export default AdminsTab;
